import PropTypes from "prop-types";
import { getFullPixelatedConfig } from "../config/config";
import { CacheManager } from "../foundation/cache-manager";
import { getDomain } from "../foundation/utilities";
import { smartFetch } from "../foundation/smartfetch";
import { buildUrl } from "../foundation/urlbuilder";
import { getCloudinaryRemoteFetchURL as getImg } from "../integrations/cloudinary";
import { getPolicyRouteUrl } from "../foundation/schema.functions";
const debug = false;
/**
 * getEbayShoppingCartItem — Map a raw eBay item into the cart-item shape used by the shopping cart.
 *
 * @param {object} [props.thisItem] - Raw eBay item object.
 * @param {string} [props.cloudinaryProductEnv] - Optional Cloudinary product environment.
 * @param {object} [props.apiProps] - eBay API properties.
 */
getEbayShoppingCartItem.propTypes = {
    /** Raw eBay item object */
    thisItem: PropTypes.any.isRequired,
    /** Optional Cloudinary product environment */
    cloudinaryProductEnv: PropTypes.string,
    /** eBay API properties */
    apiProps: PropTypes.any,
};
export function getEbayShoppingCartItem(props) {
    let qty;
    const thisItem = props.thisItem;
    const apiProps = props.apiProps;
    const itemCategory = apiProps?.itemCategory;
    if (thisItem.categoryId && thisItem.categoryId == itemCategory) {
        qty = 1;
    }
    else if (thisItem.categories?.[0]?.categoryId && thisItem.categories[0].categoryId == itemCategory) {
        qty = 1;
    }
    else {
        qty = 10;
    }
    return {
        itemImageURL: (thisItem.thumbnailImages && props.cloudinaryProductEnv)
            ? getImg({ url: thisItem.thumbnailImages[0].imageUrl, product_env: props.cloudinaryProductEnv })
            : (thisItem.thumbnailImages)
                ? thisItem.thumbnailImages[0].imageUrl
                : (thisItem.image && props.cloudinaryProductEnv)
                    ? getImg({ url: thisItem.image.imageUrl, product_env: props.cloudinaryProductEnv })
                    : thisItem.image?.imageUrl || '',
        itemID: thisItem.legacyItemId,
        itemURL: thisItem.itemWebUrl,
        itemTitle: thisItem.title,
        itemQuantity: qty,
        itemInventory: qty,
        itemCost: thisItem.price.value,
        itemCurrency: thisItem.price?.currency || 'USD',
        itemIsShippable: true,
        itemWeight: Number(thisItem.weight ?? 0),
        itemWeightUnit: thisItem.weightUnit || 'lb',
        itemType: 'product',
    };
}
const ebayCache = new CacheManager({
    mode: 'session',
    domain: getDomain(),
    namespace: 'ebay',
    ttl: 60 * 60 * 1000,
});
/**
 * getMergedEbayConfig — Helper function to merge provided API props with defaults and config values.
 *
 * @param providedApiProps - API properties provided by the caller.
 * @returns Merged eBay API configuration.
 */
getMergedEbayConfig.propTypes = {
    apiProps: PropTypes.object.isRequired,
};
export function getMergedEbayConfig(providedApiProps) {
    let apiProps = {
        proxyURL: '',
        baseTokenURL: '',
        baseSearchURL: '',
        qsSearchURL: '',
        baseItemURL: '',
        qsItemURL: '',
        baseAnalyticsURL: '',
        ...providedApiProps,
    };
    try {
        const config = getFullPixelatedConfig();
        if (config) {
            apiProps = {
                ...apiProps,
                ...(config.integrations?.ebay || {}),
                ...providedApiProps,
            };
            if (!apiProps.proxyURL) {
                apiProps.proxyURL = config.integrations?.global?.proxyUrl || '';
            }
        }
    }
    catch (e) {
        // Fail-silent, use provided props
    }
    return apiProps;
}
/**
 * getEbayAppToken — PropType definitions for fetching an eBay application token.
 *
 * @param {object} props.apiProps - eBay API configuration properties required to fetch the token.
 */
getEbayAppToken.propTypes = {
    apiProps: PropTypes.object.isRequired,
};
export function getEbayAppToken(props) {
    const apiProps = getMergedEbayConfig(props.apiProps);
    return (async () => {
        if (debug)
            console.log("Fetching Token");
        try {
            const data = await smartFetch(apiProps.baseTokenURL, {
                proxy: apiProps.proxyURL
                    ? {
                        url: apiProps.proxyURL,
                        forceProxy: true,
                        fallbackOnCors: true,
                    }
                    : undefined,
                requestInit: {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': 'Basic ' + (typeof btoa === 'function'
                            ? btoa(`${apiProps.appId}:${apiProps.appCertId}`)
                            : typeof Buffer !== 'undefined'
                                ? Buffer.from(`${apiProps.appId}:${apiProps.appCertId}`, 'utf-8').toString('base64')
                                : (() => { throw new Error('No base64 encoder available'); })()),
                    },
                    body: new URLSearchParams({
                        grant_type: 'client_credentials',
                        scope: apiProps.tokenScope,
                    }),
                },
            });
            const accessToken = data.access_token;
            if (debug)
                console.log("Fetched eBay Access Token:", accessToken);
            return accessToken;
        }
        catch (error) {
            console.error('Error fetching token:', error);
        }
    })();
}
/**
 * getEbayBrowseSearch — PropType definitions for the eBay browse search helper.
 *
 * @param {object} props.apiProps - eBay API configuration properties required to perform the search.
 * @param {string} props.token - eBay application token for authentication.
 */
getEbayBrowseSearch.propTypes = {
    apiProps: PropTypes.object.isRequired,
    token: PropTypes.string.isRequired,
};
export function getEbayBrowseSearch(props) {
    const apiProps = getMergedEbayConfig(props.apiProps);
    const fetchData = async (token) => {
        const fullURL = apiProps.baseSearchURL + apiProps.qsSearchURL;
        const cacheKey = `search_${fullURL}`;
        const cached = ebayCache.get(cacheKey);
        if (cached) {
            if (debug)
                console.log("Returning cached eBay Search Data", cacheKey);
            return cached;
        }
        if (debug)
            console.log("Fetching ebay API Browse Search Data");
        try {
            const data = await smartFetch(buildUrl({
                baseUrl: fullURL,
                proxyUrl: apiProps.proxyURL,
            }), {
                requestInit: {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'X-EBAY-C-MARKETPLACE-ID': 'EBAY_US',
                        'X-EBAY-C-ENDUSERCTX': 'affiliateCampaignId=<ePNCampaignId>,affiliateReferenceId=<referenceId>',
                        'X-EBAY-SOA-SECURITY-APPNAME': 'BrianWha-Pixelate-PRD-1fb4458de-1a8431fe',
                    }
                }
            });
            if (debug)
                console.log("Fetched eBay API Browse Search Data:", data);
            ebayCache.set(cacheKey, data);
            return data;
        }
        catch (error) {
            console.error('Error fetching data:', error);
        }
    };
    return fetchData(props.token);
}
/**
 * getEbayBrowseItem — PropType definitions for the eBay browse item helper.
 *
 * @param {object} props.apiProps - eBay API configuration properties required to fetch the item details.
 * @param {string} props.token - eBay API access token required for authentication.
 */
getEbayBrowseItem.propTypes = {
    apiProps: PropTypes.object.isRequired,
    token: PropTypes.string.isRequired,
};
export function getEbayBrowseItem(props) {
    const apiProps = getMergedEbayConfig(props.apiProps);
    const fetchData = async (token) => {
        const fullURL = (apiProps.baseItemURL ?? '') + (apiProps.qsItemURL ?? '');
        const cacheKey = `item_${fullURL}`;
        const cached = ebayCache.get(cacheKey);
        if (cached) {
            if (debug)
                console.log("Returning cached eBay Item Data", cacheKey);
            return cached;
        }
        if (debug)
            console.log("Fetching ebay API Browse Item Data");
        try {
            const data = await smartFetch(buildUrl({
                baseUrl: fullURL,
                proxyUrl: apiProps.proxyURL,
            }), {
                requestInit: {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'X-EBAY-C-MARKETPLACE-ID': 'EBAY_US',
                        'X-EBAY-C-ENDUSERCTX': 'affiliateCampaignId=<ePNCampaignId>,affiliateReferenceId=<referenceId>',
                        'X-EBAY-SOA-SECURITY-APPNAME': 'BrianWha-Pixelate-PRD-1fb4458de-1a8431fe',
                    }
                }
            });
            if (debug)
                console.log("Fetched eBay Item Data:", data);
            ebayCache.set(cacheKey, data);
            return data;
        }
        catch (error) {
            console.error('Error fetching data:', error);
        }
    };
    return fetchData(props.token);
}
/**
 * getEbayRateLimits — PropType definitions for the eBay rate limit helper.
 *
 * @param {object} props.apiProps - eBay API configuration properties required to fetch rate limit information.
 * @param {string} props.token - eBay API access token required for authentication.
 */
getEbayRateLimits.propTypes = {
    apiProps: PropTypes.object.isRequired,
    token: PropTypes.string.isRequired,
};
export function getEbayRateLimits(props) {
    const apiProps = getMergedEbayConfig(props.apiProps);
    const fetchAllLimits = async (token) => {
        if (debug)
            console.log("Fetching all eBay API Rate Limits");
        try {
            const rateLimitUrl = buildUrl({
                baseUrl: apiProps.baseAnalyticsURL,
                pathSegments: ['rate_limit'],
                proxyUrl: apiProps.proxyURL,
            });
            const userRateLimitUrl = buildUrl({
                baseUrl: apiProps.baseAnalyticsURL,
                pathSegments: ['user_rate_limit'],
                proxyUrl: apiProps.proxyURL,
            });
            const [rateLimitRes, userRateLimitRes] = await Promise.all([
                smartFetch(rateLimitUrl, {
                    responseType: 'ok',
                    requestInit: {
                        method: 'GET',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                        },
                    },
                }),
                smartFetch(userRateLimitUrl, {
                    responseType: 'ok',
                    requestInit: {
                        method: 'GET',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                        },
                    },
                }),
            ]);
            if (!rateLimitRes.ok || !userRateLimitRes.ok) {
                throw new Error(`HTTP error! rate_limit: ${rateLimitRes.status}, user_rate_limit: ${userRateLimitRes.status}`);
            }
            const [rateLimit, userRateLimit] = await Promise.all([
                rateLimitRes.json(),
                userRateLimitRes.json(),
            ]);
            const combinedData = {
                rate_limit: rateLimit,
                user_rate_limit: userRateLimit,
            };
            if (debug)
                console.log("Fetched Combined eBay Rate Limit Data:", combinedData);
            return combinedData;
        }
        catch (error) {
            console.error('Error fetching rate limits:', error);
        }
    };
    return fetchAllLimits(props.token);
}
/**
 * getEbayItems — PropType definitions for the eBay items list helper.
 *
 * @param {object} props.apiProps - eBay API configuration properties required to fetch the items list.
 * @param {string} props.token - eBay API access token required for authentication.
 */
getEbayItems.propTypes = {
    apiProps: PropTypes.object.isRequired,
};
export async function getEbayItems(props) {
    const apiProps = getMergedEbayConfig(props.apiProps);
    try {
        const response = await getEbayAppToken({ apiProps: apiProps });
        if (!response) {
            console.error('Unable to fetch eBay app token; aborting eBay item search.');
            return {};
        }
        if (debug)
            console.log("eBay App Token Response:", response);
        const data = await getEbayBrowseSearch({ apiProps: apiProps, token: response });
        if (debug)
            console.log("eBay Browse Search Data:", data);
        return data;
    }
    catch (error) {
        console.error("Failed to fetch eBay Items:", error);
    }
    return {};
}
/**
 * getEbayItem — PropType definitions for the eBay single item helper.
 *
 * @param {object} props.apiProps - eBay API configuration properties required to fetch the item details.
 */
getEbayItem.propTypes = {
    apiProps: PropTypes.object.isRequired,
};
export async function getEbayItem(props) {
    const apiProps = getMergedEbayConfig(props.apiProps);
    try {
        const response = await getEbayAppToken({ apiProps: apiProps });
        if (!response) {
            console.error('Unable to fetch eBay app token; aborting eBay item details fetch.');
            return {};
        }
        if (debug)
            console.log("eBay App Token Response:", response);
        const data = await getEbayBrowseItem({ apiProps: apiProps, token: response });
        if (debug)
            console.log("eBay Browse Item Data:", data);
        return data;
    }
    catch (error) {
        console.error("Failed to fetch eBay Items:", error);
    }
    return {};
}
export function getEbayItemsSearch(props) {
    const apiProps = getMergedEbayConfig(props.apiProps);
    const fetchData = async (token) => {
        const fullURL = apiProps.baseSearchURL + apiProps.qsSearchURL;
        const cacheKey = `search_${fullURL}`;
        const cached = ebayCache.get(cacheKey);
        if (cached) {
            if (debug)
                console.log("Returning cached eBay Search Data", cacheKey);
            return cached;
        }
        if (debug)
            console.log("Fetching ebay API Items Search Data");
        try {
            const data = await smartFetch(apiProps.proxyURL + encodeURIComponent(fullURL), {
                requestInit: {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'X-EBAY-C-MARKETPLACE-ID': 'EBAY_US',
                        'X-EBAY-C-ENDUSERCTX': 'affiliateCampaignId=<ePNCampaignId>,affiliateReferenceId=<referenceId>',
                        'X-EBAY-SOA-SECURITY-APPNAME': 'BrianWha-Pixelate-PRD-1fb4458de-1a8431fe',
                    },
                },
            });
            ebayCache.set(cacheKey, data);
            return data;
        }
        catch (error) {
            console.error('Error fetching data:', error);
        }
    };
    return fetchData(props.token);
}
getEbayProductSchema.propTypes = {
    item: PropTypes.object.isRequired,
    brandName: PropTypes.string,
    siteUrl: PropTypes.string,
    siteInfo: PropTypes.object,
    routes: PropTypes.any,
};
export function getEbayProductSchema(props) {
    const item = props.item;
    const siteInfo = props.siteInfo;
    const brandName = props.brandName || item.brand?.name || siteInfo?.brand?.name || siteInfo?.name || 'eBay';
    const siteUrl = props.siteUrl || item.itemWebUrl || '';
    const policyUrl = getPolicyRouteUrl(props.routes, siteInfo);
    if (!item || !item.title || !item.price) {
        return null;
    }
    const primaryImage = item.image?.imageUrl || item.thumbnailImages?.[0]?.imageUrl || '';
    const images = [];
    if (primaryImage)
        images.push(primaryImage);
    if (Array.isArray(item.thumbnailImages)) {
        for (const thumbnail of item.thumbnailImages) {
            if (thumbnail?.imageUrl && !images.includes(thumbnail.imageUrl)) {
                images.push(thumbnail.imageUrl);
            }
        }
    }
    if (Array.isArray(item.additionalImages)) {
        for (const extra of item.additionalImages) {
            if (extra?.imageUrl && !images.includes(extra.imageUrl)) {
                images.push(extra.imageUrl);
            }
        }
    }
    const sku = item.legacyItemId || item.itemId || '';
    const mpn = item.mpn || sku || undefined;
    const gtin = item.gtin || sku || undefined;
    const priceValue = item.price?.value ?? item.price?.__value__ ?? item.buyingOptions?.[0]?.price?.value ?? '';
    const currency = item.price?.currency || item.price?.currencyCode || 'USD';
    return {
        name: item.title,
        image: images,
        description: item.description || '',
        sku: sku || item.itemId,
        brand: {
            name: brandName,
        },
        mpn,
        gtin,
        shippingDetails: {
            isShippable: Boolean(item.shippingOptions?.length || item.shipping || item.weight || item.weightUnit),
            shippingOptions: item.shippingOptions,
            shipping: item.shipping,
            weight: item.weight,
            weightUnit: item.weightUnit,
        },
        ...(policyUrl ? { hasMerchantReturnPolicy: policyUrl } : {}),
        offers: {
            url: siteUrl,
            priceCurrency: currency,
            price: priceValue,
            availability: 'https://schema.org/InStock',
            itemCondition: item.condition ? `https://schema.org/${item.condition.replace(/\s+/g, '')}` : 'https://schema.org/NewCondition',
        },
    };
}
